/**
 * Canvas State Schema — serializable format for session diagrams.
 * Used by: SessionCanvas (editor), SessionCanvasPreview (read-only),
 * voice-to-session (AI output), drill library (stored visuals).
 */
import { z } from 'zod';

// ============================================================================
// Zod Schema
// ============================================================================

const FieldPresetSchema = z.enum(['full', 'half', 'penalty', 'custom']);

/** Canvas object types. Exported as a const array so the fidelity audit
 *  and enum-parity tests can iterate — keep this in sync with the 2D
 *  drawer (DrillDiagram2D.tsx) and the 3D equipment dispatch
 *  (Canvas3D/equipment.ts + playerMesh.ts). */
export const CANVAS_OBJECT_TYPES = [
  'player', 'opponent',
  // Legacy generic types (kept for backward compat with saved canvases)
  'cone', 'ball', 'goal', 'mannequin', 'pole',
  // New specific equipment from GLB pipeline
  'cone_disc', 'cone_training', 'cone_traffic',
  'corner_flag', 'crossbar',
  'hurdle_mini', 'hurdle_med', 'agility_ladder', 'bench', 'rebounder',
  'goal_mini', 'goal_7v7', 'goal_9v9', 'goal_full',
] as const;
const ObjectTypeSchema = z.enum(CANVAS_OBJECT_TYPES as unknown as [string, ...string[]]);

/** Arrow types. Same sync rule as CANVAS_OBJECT_TYPES. */
export const CANVAS_ARROW_TYPES = ['run', 'pass', 'dribble'] as const;
const ArrowTypeSchema = z.enum(CANVAS_ARROW_TYPES as unknown as [string, ...string[]]);

const CanvasObjectSchema = z.object({
  id: z.string(),
  type: ObjectTypeSchema,
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  label: z.string().optional(),
  color: z.string(),
  rotation: z.number(),
  gender: z.enum(['male', 'female']).optional(),
  ageGroup: z.enum(['youth', 'teen', 'adult']).optional(),
  kickingFoot: z.enum(['left', 'right', 'both']).optional(),
  receivingFoot: z.enum(['left', 'right', 'either']).optional(),
  receivingSurface: z.enum(['inside', 'outside', 'sole', 'laces']).optional(),
  bodyShape: z.enum(['open', 'half-turn', 'back-to-goal', 'sideways']).optional(),
});

const MovementArrowSchema = z.object({
  id: z.string(),
  fromId: z.string(),
  toX: z.number().min(0).max(1),
  toY: z.number().min(0).max(1),
  toId: z.string().optional(),
  type: ArrowTypeSchema,
});

/** Actions a coach can record on the 2D canvas. Kept in sync with the
 *  in-memory `ActionType` in SessionEditor.tsx. */
export const CANVAS_STEP_TYPES = ['pass', 'passMove', 'oneTwo', 'throughBall', 'dribble', 'runOff'] as const;
const StepTypeSchema = z.enum(CANVAS_STEP_TYPES as unknown as [string, ...string[]]);

/** Persisted step. Carries enough detail that a save → load round-trip
 *  preserves the coach's motion tuning (speeds, overrun, lofted, free-
 *  position targets). Legacy saves with only type/from/to/pos fields
 *  still parse — every motion field is optional. */
const RecordedStepSchema = z.object({
  id: z.string().optional(),
  type: StepTypeSchema,
  fromId: z.string(),
  toId: z.string().optional(),
  fromPos: z.object({ x: z.number(), y: z.number() }).optional(),
  toPos: z.object({ x: z.number(), y: z.number() }).optional(),
  /** Free position target — used by throughBall, dribble, runOff. */
  targetX: z.number().optional(),
  targetY: z.number().optional(),
  /** Passer's run end position — used by passMove and oneTwo. */
  runX: z.number().optional(),
  runY: z.number().optional(),
  /** m/s — ball speed. Defaults to 15 when missing. */
  passSpeed: z.number().optional(),
  /** m/s — player movement speed. Defaults to 5 when missing. */
  runSpeed: z.number().optional(),
  /** Normalised field units the passer runs PAST the receiver (passMove). */
  overrun: z.number().optional(),
  lofted: z.boolean().optional(),
});

export const CanvasStateSchema = z.object({
  version: z.literal(1),
  field: z.object({
    preset: FieldPresetSchema,
    customWidth: z.number().optional(),
    customLength: z.number().optional(),
  }),
  objects: z.array(CanvasObjectSchema),
  arrows: z.array(MovementArrowSchema),
  steps: z.array(RecordedStepSchema),
});

// ============================================================================
// Types (inferred from Zod)
// ============================================================================

export type CanvasState = z.infer<typeof CanvasStateSchema>;
export type CanvasObject = z.infer<typeof CanvasObjectSchema>;
export type MovementArrow = z.infer<typeof MovementArrowSchema>;
export type RecordedStep = z.infer<typeof RecordedStepSchema>;
export type FieldPreset = z.infer<typeof FieldPresetSchema>;
export type ObjectType = z.infer<typeof ObjectTypeSchema>;
export type ArrowType = z.infer<typeof ArrowTypeSchema>;

// ============================================================================
// Serialization
// ============================================================================

interface CanvasReactState {
  preset: FieldPreset;
  customW: string;
  customL: string;
  objects: CanvasObject[];
  arrows: MovementArrow[];
  steps: RecordedStep[];
}

/**
 * Serialize current React state into a persistable CanvasState JSON.
 */
export function serializeCanvas(state: CanvasReactState): CanvasState {
  return {
    version: 1,
    field: {
      preset: state.preset,
      ...(state.preset === 'custom' ? {
        customWidth: Number(state.customW) || 15,
        customLength: Number(state.customL) || 15,
      } : {}),
    },
    objects: state.objects,
    arrows: state.arrows,
    steps: state.steps,
  };
}

/**
 * Deserialize a CanvasState JSON into React state values.
 */
export function deserializeCanvas(data: CanvasState): CanvasReactState {
  return {
    preset: data.field.preset,
    customW: String(data.field.customWidth ?? 15),
    customL: String(data.field.customLength ?? 15),
    objects: data.objects,
    arrows: data.arrows,
    steps: data.steps,
  };
}

/**
 * Validate and parse raw JSON into a CanvasState.
 * Returns null if validation fails.
 */
export function parseCanvasState(raw: unknown): CanvasState | null {
  const result = CanvasStateSchema.safeParse(raw);
  return result.success ? result.data : null;
}

/**
 * Create an empty canvas state for a given field preset.
 */
export function createEmptyCanvas(
  preset: FieldPreset = 'custom',
  width = 15,
  length = 15,
): CanvasState {
  return {
    version: 1,
    field: { preset, customWidth: width, customLength: length },
    objects: [],
    arrows: [],
    steps: [],
  };
}
